import React, { createContext, useState } from "react";
import { ApiConfig } from "config/ApiConfig";
import axios from "axios";
import { toast } from "react-toastify";
import subDays from "date-fns/subDays";
import startOfMonth from "date-fns/startOfMonth";
import { ShowToastError } from "components/Widgets/ShowToastError";
import Cookies from 'js-cookie';

const QAContext = createContext();

const QAProvider = ({ children }) => {
  // --------------------#Combined -----------------------

  const [dateRange, setDateRange] = useState([
    startOfMonth(new Date()),
    new Date(),
  ]);
  const maxDateAllowed = new Date(); // Maximum date is today
  // const minDateAllowed = subDays(new Date(), 89); // Minimum date is 89 days ago (last 90 days)
  const minDateAllowed = subDays(new Date(), 365); // Minimum date is 365 days ago (last 365 days)

  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [modalTitle, setModalTitle] = useState("");

  // Function to handle modal open
  const handleOpenModal = (component, title) => {
    setModalContent(component);
    setModalTitle(title);
    setShowModal(true);
  };

  // Function to handle modal close
  const handleCloseModal = () => {
    setShowModal(false);
    setModalContent(null);
  };

  // --------------------#Overall Team/Group View -----------------------
  const [viewOptionLevel1, setViewOptionLevel1] = useState("qaoverallview");
  const [groupTeamViewOption, setGroupTeamViewOption] = useState("group");

  // --------------------#Overall View -----------------------
  const [overallData, setOverallData] = useState({});

  const [isLoadingOverallData, setIsLoadingOverallData] = useState(false);

  const fetchOverallData = async () => {
    // Validate dateRange
    if (!Array.isArray(dateRange) || dateRange.length !== 2) {
      console.error('Invalid dateRange:', dateRange);
      // Set default date range if invalid
      setDateRange([startOfMonth(new Date()), new Date()]);
      return;
    }

    const [startDate, endDate] = dateRange;

    const apiData = {
      start_date: startDate,
      end_date: endDate,
      view: groupTeamViewOption,
    };

    const token = Cookies.get('access_token'); 
    console.log("Token:", token);
    const config = {
      headers: {
        "Access-Control-Allow-Origin": "*",
        Authorization: `Bearer ${token}`,
      },
      withCredentials: true,
    };

    try {
      setIsLoadingOverallData(true);

      const response = await axios.post(
        ApiConfig.qaOverallView,
        apiData,
        config
      );
      setOverallData(response?.data);
    } catch (error) {
      ShowToastError({ error });
    } finally {
      setIsLoadingOverallData(false);
    }
  };
  // --------------------#TeamGroup View -----------------------
  const [groupTeamData, setGroupTeamData] = useState({});

  const [isLoadingGroupTeamData, setIsLoadingGroupTeamData] = useState(false);

  const fetchGroupTeamData = async () => {
    const [startDate, endDate] = dateRange;

    // Initialize variables for dynamic URL and API data
    const apiData = {
      start_date: startDate,
      end_date: endDate,
      view: groupTeamViewOption,
    };
    const config = {
      headers: { "Access-Control-Allow-Origin": "*" },
      withCredentials: true,
    };

    try {
      setIsLoadingGroupTeamData(true);

      const response = await axios.post(
        ApiConfig.qaTeamgroupView,
        apiData,
        config
      );

      setGroupTeamData(response.data);
    } catch (error) {
      ShowToastError({ error });
    } finally {
      setIsLoadingGroupTeamData(false);
    }
  };
  // --------------------#Agent View -----------------------

  const [searchAgent, setSearchAgent] = useState({
    name: "",
    role: "",
    email: "",
  });

  const [agentData, setAgentData] = useState({});

  const [isLoadingAgentData, setIsLoadingAgentData] = useState(false);

  const fetchAgentData = async () => {
    const [startDate, endDate] = dateRange;

    const apiData = {
      start_date: startDate,
      end_date: endDate,
      agent_email: searchAgent.email,
    };

    const config = {
      headers: { "Access-Control-Allow-Origin": "*" },
      withCredentials: true,
    };

    try {
      setIsLoadingAgentData(true);
      const response = await axios.post(ApiConfig.qaAgentView, apiData, config);
      setAgentData(response?.data);
    } catch (error) {
      ShowToastError({ error });
    } finally {
      setIsLoadingAgentData(false);
    }
  };

  // --------------------#Ticket View -----------------------

  const [searchTicket, setSearchTicket] = useState("");
  const [ticketData, setTicketData] = useState({});

  // ticket review (filter)
  const [selectedReason, setSelectedReason] = useState("");

  const [isLoadingTicketData, setIsLoadingTicketData] = useState(false);

  const fetchTicketData = async () => {
    if (!searchTicket || searchTicket.trim() === "") {
      toast.error("Please enter a valid ticket ID.");
      return;
    }

    const apiData = {
      ticket_id: searchTicket.toString(),
    };

    const config = {
      headers: { "Access-Control-Allow-Origin": "*" },
      withCredentials: true,
    };

    try {
      setIsLoadingTicketData(true);
      const response = await axios.post(
        ApiConfig.qaTicketView,
        apiData,
        config
      );

      setTicketData(response?.data);
    } catch (error) {
      ShowToastError({ error });
    } finally {
      setIsLoadingTicketData(false);
    }
  };

  return (
    <QAContext.Provider
      value={{
        // --------------------#Combined -----------------------
        setDateRange,
        dateRange,
        maxDateAllowed,
        minDateAllowed,
        showModal,
        modalContent,
        modalTitle,
        handleOpenModal,
        handleCloseModal,
        // --------------------#Overall Team/Group View -----------------------
        setGroupTeamViewOption,
        groupTeamViewOption,
        viewOptionLevel1,
        setViewOptionLevel1,
        // --------------------#Overall View ----------------------
        overallData,
        fetchOverallData,
        isLoadingOverallData,
        // --------------------#TeamGroup View -----------------------
        groupTeamData,
        fetchGroupTeamData,
        isLoadingGroupTeamData,
        // --------------------#Agent View -----------------------
        fetchAgentData,
        agentData,
        setSearchAgent,
        searchAgent,
        isLoadingAgentData,
        // --------------------#Ticket View -----------------------
        fetchTicketData,
        ticketData,
        setSearchTicket,
        searchTicket,
        isLoadingTicketData,
        selectedReason,
        setSelectedReason,
      }}
    >
      {children}
    </QAContext.Provider>
  );
};

export { QAContext, QAProvider };
